import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { useCoreApp } from "@/contexts/CoreAppContext";
import FilePreview from "@/components/file/Preview";
import { FileCarousel } from "@/components/file/Carousel/FileCarousel";
import { BoundingBoxStrategyImpl } from "@utils/annotationStrategies/BoundingBox";
import { FilePreviewProvider } from "@/contexts/FilePreviewProvider";
import { useTestWithMoreFiles } from "@/useTestWithMoreFiles";
import {
    useCurrentMedia,
    useSnapshotUserAnnotations,
    useClearUserAnnotationsSnapshot,
    useUserAnnotationsSnapshot,
    useMediaStore,
    useFlattenedVideoSourceId,
    useSetFlattenedVideoSourceId,
    useStore,
    useAnyLabelHasDiffs
} from "@/store";
import { CurrentMedia } from "@/store/slices/mediaSlice";
import { AnnotationSource } from "@/types/annotation";
import { MediaItem, MediaItemStatus } from "@/types/media";
import { useMedia } from "@/contexts/MediaContext";
import { useReviewGridView } from "@/contexts/ReviewGridViewContext";
import { ReviewTabs, ReviewTab, REVIEW_TAB_TO_ANNOTATION_SOURCE } from "./ReviewTabs";
import { useReviewTab } from "./ReviewContext";
import { ReviewGridView } from "./ReviewGridView";

const MIN_FILES_FOR_GRID_VIEW = 3;

function ReviewFilePreview({
    showLabels,
    fileId,
    annotationSource
}: {
    showLabels: boolean;
    fileId: string | undefined;
    annotationSource: AnnotationSource;
}) {
    return (
        <FilePreview
            key={`file-${fileId}`}
            forceHideLabels={!showLabels}
            strategy={BoundingBoxStrategyImpl}
            includeWatermark={true}
            watermarkQrUrl="https://rapid.roboflow.com"
            annotationSourceOverride={annotationSource}
        />
    );
}

export const ReviewVisualAreaContent = memo(() => {
    const { showLabels } = useCoreApp();
    const currentMedia = useCurrentMedia();
    const media = useMediaStore();
    const setCurrentMedia = useStore((state) => state.setCurrentMedia);
    const { testWithMoreFiles, uploadLimitError, clearUploadLimitError } = useTestWithMoreFiles();
    const { activeTab, setActiveTab, savedVideoMedia, setSavedVideoMedia } = useReviewTab();
    const { orderedMediaItems } = useMedia();
    const { isGridView, setIsGridView } = useReviewGridView();
    const anyLabelHasDiffs = useAnyLabelHasDiffs();
    const didInitialGridSync = useRef(false);
    const [isHovered, setIsHovered] = useState(false);

    const userAnnotationsSnapshot = useUserAnnotationsSnapshot();
    const flattenedVideoSourceId = useFlattenedVideoSourceId();
    const setFlattenedVideoSourceId = useSetFlattenedVideoSourceId();

    // Snapshot user annotations before inference overwrites them.
    // useLayoutEffect fires synchronously before useEffects (which trigger inference),
    // guaranteeing the snapshot captures the user's original annotations.
    const snapshotUserAnnotations = useSnapshotUserAnnotations();
    const clearUserAnnotationsSnapshot = useClearUserAnnotationsSnapshot();
    useLayoutEffect(() => {
        snapshotUserAnnotations();
        return () => {
            clearUserAnnotationsSnapshot();
            setFlattenedVideoSourceId(null);
        };
    }, [snapshotUserAnnotations, clearUserAnnotationsSnapshot, setFlattenedVideoSourceId]);

    useEffect(() => {
        if (didInitialGridSync.current) return;
        didInitialGridSync.current = true;
        setIsGridView(orderedMediaItems.length >= MIN_FILES_FOR_GRID_VIEW);
    }, [orderedMediaItems.length, setIsGridView]);

    const onAddMediaItems = useCallback(
        async (files: File[], onFinally?: () => void) => {
            await testWithMoreFiles(files, onFinally);
        },
        [testWithMoreFiles]
    );

    // Compute annotated frames for videos. For each video in orderedMediaItems,
    // find extracted frames that have user annotations in the snapshot.
    const annotatedFramesByVideoId = useMemo(() => {
        const result: Record<string, typeof orderedMediaItems> = {};

        for (const item of orderedMediaItems) {
            if (!item.isVideo) continue;

            const fileSnapshot = userAnnotationsSnapshot[item.id];
            if (!fileSnapshot) continue;

            const frames: typeof orderedMediaItems = [];
            for (const [frameId, annotations] of Object.entries(fileSnapshot)) {
                if (!annotations || annotations.length === 0) continue;
                const frameItem = media[frameId];
                if (frameItem && frameItem.status === MediaItemStatus.COMPLETED) {
                    frames.push(frameItem);
                }
            }

            if (frames.length > 0) {
                // Sort by segment so frames appear in timeline order
                frames.sort((a, b) => (a.metadata?.segment ?? 0) - (b.metadata?.segment ?? 0));
                result[item.id] = frames;
            }
        }

        return result;
    }, [orderedMediaItems, userAnnotationsSnapshot, media]);

    const isAnnotationsView = activeTab === ReviewTab.MyAnnotations;
    const hasVideoInMedia = orderedMediaItems.some((item) => item.isVideo);
    const isVideoFlatteningActive = isAnnotationsView && hasVideoInMedia;

    // Build the override list: replace each video with its annotated frames
    const orderedMediaItemsOverride = useMemo(() => {
        if (!isVideoFlatteningActive) return undefined;

        const result: typeof orderedMediaItems = [];
        for (const item of orderedMediaItems) {
            if (item.isVideo && annotatedFramesByVideoId[item.id]) {
                result.push(...annotatedFramesByVideoId[item.id]);
            } else if (!item.isVideo) {
                result.push(item);
            }
            // If a video has no annotated frames, it's simply omitted
        }

        return result;
    }, [isVideoFlatteningActive, orderedMediaItems, annotatedFramesByVideoId]);

    // Track whether we've already handled the tab switch to avoid re-running
    const prevIsVideoFlatteningRef = useRef(false);

    useEffect(() => {
        const wasFlattening = prevIsVideoFlatteningRef.current;
        prevIsVideoFlatteningRef.current = isVideoFlatteningActive;

        if (isVideoFlatteningActive && !wasFlattening) {
            // Switching TO annotations view with a video — save and flatten
            if (currentMedia?.file.isVideo) {
                setSavedVideoMedia(currentMedia);
                setFlattenedVideoSourceId(currentMedia.file.id);

                // Navigate to the first annotated frame (or the current frame if it has annotations)
                const videoFrames = annotatedFramesByVideoId[currentMedia.file.id];
                if (videoFrames && videoFrames.length > 0) {
                    const currentFrameInList = currentMedia.frame
                        ? videoFrames.find((f) => f.id === currentMedia.frame!.id)
                        : undefined;
                    const targetFrame = currentFrameInList ?? videoFrames[0];
                    setCurrentMedia({ file: targetFrame, frame: targetFrame, frameSegment: 0 });
                }
            }
        } else if (!isVideoFlatteningActive && wasFlattening) {
            // Switching BACK to predictions view — restore the video
            setFlattenedVideoSourceId(null);

            if (savedVideoMedia) {
                // Try to restore at the frame the user was last viewing
                const currentFrameId = currentMedia?.file.id;
                const videoItem = media[savedVideoMedia.file.id];

                if (videoItem) {
                    // Find the segment for the frame we were viewing
                    let targetSegment = savedVideoMedia.frameSegment ?? 0;
                    let targetFrame = savedVideoMedia.frame;

                    if (currentFrameId && videoItem.segmentToFrameMediaItemIdMap) {
                        for (const [seg, frameId] of Object.entries(
                            videoItem.segmentToFrameMediaItemIdMap
                        )) {
                            if (frameId === currentFrameId) {
                                targetSegment = Number(seg);
                                targetFrame = media[frameId];
                                break;
                            }
                        }
                    }

                    setCurrentMedia({
                        file: videoItem,
                        frame: targetFrame,
                        frameSegment: targetSegment
                    });
                }

                setSavedVideoMedia(null);
            }
        }
    }, [
        isVideoFlatteningActive,
        currentMedia,
        savedVideoMedia,
        annotatedFramesByVideoId,
        media,
        setCurrentMedia,
        setSavedVideoMedia,
        setFlattenedVideoSourceId
    ]);

    const annotationSource = REVIEW_TAB_TO_ANNOTATION_SOURCE[activeTab];

    const handleSelectMediaFromGrid = useCallback(
        (mediaItem: MediaItem) => {
            const storeItem = media[mediaItem.id];
            if (!storeItem) return;

            const updatedCurrentMedia: CurrentMedia = { file: storeItem };
            if (storeItem.isVideo) {
                const frame0 = storeItem.frame0Id ? media[storeItem.frame0Id] : undefined;
                if (frame0) {
                    updatedCurrentMedia.frame = frame0;
                    updatedCurrentMedia.frameSegment = 0;
                }
            } else {
                updatedCurrentMedia.frame = storeItem;
                updatedCurrentMedia.frameSegment = 0;
            }

            setCurrentMedia(updatedCurrentMedia);
            setIsGridView(false);
        },
        [media, setCurrentMedia, setIsGridView]
    );

    const toggleGridView = useCallback(() => setIsGridView((prev) => !prev), [setIsGridView]);

    return (
        <AnimatePresence mode="wait">
            <div
                className="visualArea"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div className="flex min-h-0 flex-1">
                    <FilePreviewProvider>
                        <FileCarousel
                            onAddMediaItems={onAddMediaItems}
                            uploadLimitError={uploadLimitError}
                            clearUploadLimitError={clearUploadLimitError}
                            orderedMediaItemsOverride={orderedMediaItemsOverride}
                            autoSelectDisabled={isVideoFlatteningActive}
                            showFloatingNavigation={!isGridView}
                            showVideoControls={!isGridView}
                        >
                            <div className="relative flex min-h-0 flex-1 flex-col">
                                <div
                                    className={
                                        isGridView ? "hidden" : "flex min-h-0 flex-1 flex-col"
                                    }
                                >
                                    <ReviewFilePreview
                                        showLabels={showLabels}
                                        fileId={currentMedia?.file.id}
                                        annotationSource={annotationSource}
                                    />
                                </div>
                                <ReviewGridView
                                    onSelectMedia={handleSelectMediaFromGrid}
                                    visible={isGridView}
                                    annotationSource={annotationSource}
                                    orderedMediaItemsOverride={orderedMediaItemsOverride}
                                    flattenedVideoSourceId={flattenedVideoSourceId}
                                />
                                <ReviewTabs
                                    activeTab={activeTab}
                                    onTabChange={setActiveTab}
                                    isGridView={isGridView}
                                    onToggleGridView={toggleGridView}
                                    visible={isHovered}
                                    showTabs={anyLabelHasDiffs}
                                />
                            </div>
                        </FileCarousel>
                    </FilePreviewProvider>
                </div>
            </div>
        </AnimatePresence>
    );
});

ReviewVisualAreaContent.displayName = "ReviewVisualAreaContent";
